/**
 * Firebase client.
 *
 * Only the auth product is imported, and only the functions used — the modular SDK
 * tree-shakes, so pulling in `firebase/app` and `firebase/auth` rather than the compat bundle
 * keeps this to a fraction of the full library.
 *
 * These config values are not secrets. The API key identifies the project to Google; access is
 * controlled by Firebase security rules and by our own API verifying the ID token's audience.
 */
import { initializeApp } from "firebase/app";
import {
  browserLocalPersistence,
  createUserWithEmailAndPassword,
  getAuth,
  GoogleAuthProvider,
  onIdTokenChanged,
  sendPasswordResetEmail,
  setPersistence,
  signInWithEmailAndPassword,
  signInWithPopup,
  signOut,
  updateProfile,
  type User as FirebaseUser,
} from "firebase/auth";

/**
 * Vite inlines these at *build* time, so a missing one isn't a runtime misconfiguration you can
 * fix on the host — it's baked into the bundle. Firebase's own response is
 * `auth/invalid-api-key` thrown from `getAuth()` at module scope, which means the module never
 * finishes evaluating, React never mounts, the error boundary never runs, and the visitor gets
 * a blank page with one opaque line in the console. Check first and say what's actually wrong
 * and where to fix it, the same way `createDb` does on the API side.
 */
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
};

const missing = Object.entries(firebaseConfig)
  .filter(([, value]) => !value)
  .map(([key]) => `VITE_FIREBASE_${key.replace(/[A-Z]/g, (c) => `_${c}`).toUpperCase()}`);

if (missing.length) {
  throw new Error(
    `Firebase is not configured — missing ${missing.join(", ")}.\n` +
      "  Local:    cp .env.example .env, fill in the values, restart `npm run dev`\n" +
      "  Deployed: these are inlined at build time, so they must be set when `npm run build`\n" +
      "            runs — setting them on the host afterwards has no effect\n" +
      "  Values:   Firebase console → Project settings → Your apps → SDK setup and configuration",
  );
}

const app = initializeApp(firebaseConfig);

export const auth = getAuth(app);

// Survives a browser restart, so players stay signed in between gameweeks. The SDK refreshes
// the ID token in the background; we never persist the token ourselves.
void setPersistence(auth, browserLocalPersistence);

export type { FirebaseUser };

export function watchAuth(handler: (user: FirebaseUser | null) => void): () => void {
  // onIdTokenChanged rather than onAuthStateChanged: it also fires on token refresh, so a
  // long-lived tab never ends up sending an expired token.
  return onIdTokenChanged(auth, handler);
}

/** Current ID token, refreshed by the SDK when it's close to expiry. */
export async function getIdToken(): Promise<string | null> {
  return auth.currentUser ? auth.currentUser.getIdToken() : null;
}

export async function signInWithGoogle(): Promise<void> {
  const provider = new GoogleAuthProvider();
  provider.setCustomParameters({ prompt: "select_account" });
  await signInWithPopup(auth, provider);
}

export async function signInWithEmail(email: string, password: string): Promise<void> {
  await signInWithEmailAndPassword(auth, email, password);
}

export async function registerWithEmail(
  email: string,
  password: string,
  displayName: string,
): Promise<void> {
  const credential = await createUserWithEmailAndPassword(auth, email, password);
  // Firebase's own displayName is only a hint for our username step — ours is the unique one.
  if (displayName) await updateProfile(credential.user, { displayName });
}

export async function resetPassword(email: string): Promise<void> {
  await sendPasswordResetEmail(auth, email);
}

export async function signOutUser(): Promise<void> {
  await signOut(auth);
}

/**
 * Firebase error codes are not for humans ("auth/invalid-credential"). Map the ones people
 * actually hit; anything unmapped falls back to the raw message rather than a lie.
 */
export function describeAuthError(error: unknown): string {
  const code = (error as { code?: string })?.code ?? "";

  const messages: Record<string, string> = {
    "auth/invalid-credential": "Email or password is incorrect.",
    "auth/invalid-email": "That doesn't look like a valid email address.",
    "auth/user-not-found": "Email or password is incorrect.",
    "auth/wrong-password": "Email or password is incorrect.",
    "auth/email-already-in-use": "An account with that email already exists — try signing in.",
    "auth/weak-password": "Pick a password of at least 6 characters.",
    "auth/too-many-requests": "Too many attempts. Wait a moment and try again.",
    "auth/popup-closed-by-user": "Sign-in was cancelled.",
    "auth/popup-blocked": "Your browser blocked the sign-in popup. Allow popups and retry.",
    "auth/network-request-failed": "Couldn't reach Firebase. Check your connection.",
    "auth/operation-not-allowed": "That sign-in method isn't enabled for this project yet.",
    "auth/unauthorized-domain": "This domain isn't authorised in the Firebase console.",
  };

  if (messages[code]) return messages[code]!;
  return error instanceof Error ? error.message : "Something went wrong. Try again.";
}
